//HERReducer.ts
import { RunContextApp } from "framework/RCApp";
import { HER_ACTIONS_TYPES } from "store/actions/herActions";

export interface Batch {
  id: string;
  pilotId: number;
  batchSize: number;
  createdAt: string;
  updatedAt: string;
}

export interface NotificationEventData {
  billEndTime?: string;
  billStartTime?: string;
  herModulesOrderKey?: string;
  herNbiIds?: string;
  isSolar?: string;
  report_template_order?: string;
  user_persona?: string;
  withWelcome?: string;
  [key: string]: any; // Allow additional dynamic properties
}

export interface Notification {
  notificationId: string;
  userId: string;
  pilotId: number;
  deliveryMode: string;
  notificationType: string;
  deliveryDestination: string;
  notificationTitle: string;
  status: string;
  lastOpenedTimestamp: number;
  lastClickedTimestamp: number;
  lastUnsubscribeClickTimestamp: number;
  generationTimestamp: number;
  deliveredTimestamp: number;
  eventData: NotificationEventData;
  measurementType: string;
  triggerId: string;
}

export interface BatchNotifications {
  [batchId: string]: {
    notifications: Notification[];
    totalCount: number;
    fetchedAt: number; // timestamp
  };
}

export interface NotificationDetail {
  notificationTitle: string;
  notificationText: string;
  notificationBody: string;
  metaData: {
    userId: string;
    notificationId: string;
    deliveryMode: string;
    eventName: string;
    pilotId: number;
    [key: string]: any;
  };
  fetchedAt: number; // timestamp
}

export interface NotificationDetailsCache {
  [notificationId: string]: NotificationDetail;
}

export interface HERReducerState {
  batches: Batch[];
  batchNotifications: BatchNotifications;
  notificationDetails: NotificationDetailsCache; // Cache for individual notification details
  // Separate loading states
  isBatchesLoading: boolean;
  isNotificationsLoading: boolean;
  // Separate error states
  batchesError?: string;
  notificationsError?: string;

  [key: string]: any;
}

export interface HERReducerAction {
  type: string;
  payload?: any;
  batchId?: string;
  notifications?: Notification[];
  totalCount?: number;
}

const defaultState: HERReducerState = {
  batches: [],
  batchNotifications: {},
  notificationDetails: {},
  isBatchesLoading: false,
  isNotificationsLoading: false,
};

export const herReducer = (rc: RunContextApp) => (
  state = { ...defaultState } as HERReducerState,
  action: HERReducerAction
) => {
  switch (action.type) {
    case HER_ACTIONS_TYPES.SET_BATCHES:
      state = {
        ...state,
        batches: action.payload || [],
        isBatchesLoading: false,
      };
      break;
    case HER_ACTIONS_TYPES.SET_BATCH_NOTIFICATIONS:
      if (action.batchId) {
        state = {
          ...state,
          batchNotifications: {
            ...state.batchNotifications,
            [action.batchId]: {
              notifications: action.notifications || [],
              totalCount: action.totalCount || 0,
              fetchedAt: Date.now(),
            },
          },
          isNotificationsLoading: false,
        };
      }
      break;
    case HER_ACTIONS_TYPES.SET_NOTIFICATION_DETAIL:
      if (action.payload?.notificationId && action.payload?.detail) {
        state = {
          ...state,
          notificationDetails: {
            ...state.notificationDetails,
            [action.payload.notificationId]: {
              ...action.payload.detail,
              fetchedAt: Date.now(),
            },
          },
        };
      }
      break;
    case HER_ACTIONS_TYPES.REMOVE_BATCH:
      if (action.payload) {
        const batchIdToRemove = action.payload;
        // Remove batch from batches array
        const updatedBatches = state.batches.filter(batch => batch.id !== batchIdToRemove);
        state = {
          ...state,
          batches: updatedBatches,
        };
      }
      break;
    case HER_ACTIONS_TYPES.SET_BATCHES_LOADING:
      state = {
        ...state,
        isBatchesLoading: action.payload,
      };
      break;
    case HER_ACTIONS_TYPES.SET_NOTIFICATIONS_LOADING:
      state = {
        ...state,
        isNotificationsLoading: action.payload,
      };
      break;
    case HER_ACTIONS_TYPES.SET_BATCHES_ERROR:
      state = {
        ...state,
        batchesError: action.payload,
        isBatchesLoading: false,
      };
      break;
    case HER_ACTIONS_TYPES.SET_NOTIFICATIONS_ERROR:
      state = {
        ...state,
        notificationsError: action.payload,
        isNotificationsLoading: false,
      };
      break;
    case HER_ACTIONS_TYPES.CLEAR_BATCHES_ERROR:
      state = {
        ...state,
        batchesError: undefined,
      };
      break;
    case HER_ACTIONS_TYPES.CLEAR_NOTIFICATIONS_ERROR:
      state = {
        ...state,
        notificationsError: undefined,
      };
      break;
    default:
      break;
  }
  return state;
};

